import { createSelector } from 'reselect';
import {
  isMissionActive,
  formatMaintenanceMission,
  getVisibleMutualFundMaintenanceMissions,
  rollupMissionsByRegistration,
  getPendingBrokerageMissionsWithNoActiveSweepMissions,
  sortMissionsByCreationDate,
  getVisibleMaintenanceMissionsFromRollup,
  getVisibleBrokerageMaintenanceMissions,
  getBrokerageMaintenanceMissionsBySweepId,
  isMissionComplete
} from 'components/shared/ClientMissions/utils/MissionUtils';
import { getRequestStatus } from 'state/requests/RequestStatusSelectors';

export const getMissions = (state) => state.missions;
export const getFundingMethod = (state) => state.fundingMethod;
export const getStepModalState = (state) => state.stepModal;

export const getEligibleMissions = createSelector(getMissions, (missions) => {
  return missions;
});

export const getActiveFundingMissions = createSelector(getMissions, (missions) => {
  return sortMissionsByCreationDate(missions.filter(mission => isMissionActive(mission.onboardingFundingMission.status)));
});

export const getActiveMaintenanceMissions = createSelector(
  getMissions,
  (missions) => {
    let maintenanceMissions = [];

    if(missions && missions.length) {
      const activeMutualFundMaintenanceMissions = getVisibleMutualFundMaintenanceMissions(missions);
      const activeBrokerageMaintenanceMissions = getVisibleBrokerageMaintenanceMissions(missions);

      const groupedMutualFundMaintenanceMissions = rollupMissionsByRegistration(activeMutualFundMaintenanceMissions);
      const groupedBrokerageMaintenanceMissions = rollupMissionsByRegistration(activeBrokerageMaintenanceMissions);

      Object.keys(groupedMutualFundMaintenanceMissions).forEach(missionKey => {
        if(groupedMutualFundMaintenanceMissions[missionKey] && groupedMutualFundMaintenanceMissions[missionKey].length) {
          const pendingMaintenanceMissions = getVisibleMaintenanceMissionsFromRollup(groupedMutualFundMaintenanceMissions[missionKey]);
          //Brokerage and MF registrationKeys are different so we need to group them differently even though they are related
          const brokerageMaintenanceMissionsBySweepId = getBrokerageMaintenanceMissionsBySweepId(activeBrokerageMaintenanceMissions, groupedMutualFundMaintenanceMissions[missionKey]);
          if(brokerageMaintenanceMissionsBySweepId && brokerageMaintenanceMissionsBySweepId.length) {
            brokerageMaintenanceMissionsBySweepId.forEach(m => pendingMaintenanceMissions.push(m));
          }
          //Save off a template mission to use if we need to build a maintenance mission
          const templateMission = groupedMutualFundMaintenanceMissions[missionKey][0];

          //If there are active maintenance missions to show
          if(pendingMaintenanceMissions && pendingMaintenanceMissions.length) {
            //The agreements in the rollup should have the same account type and owners so use the template
            maintenanceMissions.push(formatMaintenanceMission(templateMission, pendingMaintenanceMissions))
          }
        }
      });

      Object.keys(groupedBrokerageMaintenanceMissions).forEach(brkMissionKey => {
        if(groupedBrokerageMaintenanceMissions[brkMissionKey] && groupedBrokerageMaintenanceMissions[brkMissionKey].length) {
          const pendingBrokerageMissions = getPendingBrokerageMissionsWithNoActiveSweepMissions(groupedBrokerageMaintenanceMissions[brkMissionKey], activeMutualFundMaintenanceMissions);

          const templateMission = groupedBrokerageMaintenanceMissions[brkMissionKey][0];

          if(pendingBrokerageMissions && pendingBrokerageMissions.length) {
            //The agreements in the rollup should have the same account type and owners so use the template
            maintenanceMissions.push(formatMaintenanceMission(templateMission, pendingBrokerageMissions))
          }
        }
      });
    }
    return sortMissionsByCreationDate(maintenanceMissions);
});

export const getActiveMissions = createSelector(
  getActiveFundingMissions,
  getActiveMaintenanceMissions,
  (fundingMissions, maintenanceMissions) => {
    return fundingMissions.concat(maintenanceMissions);
  }
);

export const getRawActiveMaintenanceMissions = createSelector(
  getMissions,
  (missions) => {
    return missions.filter(mission => isMissionComplete(mission.onboardingFundingMission.status) && isMissionActive(mission.onboardingMaintenanceMission.status));
  }
);

export const getStepModalOpen = createSelector(
  getStepModalState,
  (stepModalState) => {
    return stepModalState?.open;
  }
);

export const getStepModalAgreementID = createSelector(
  getStepModalState,
  (stepModalState) => {
    return stepModalState?.agreementID;
  }
);

export const getStepModalIndex = createSelector(
  getStepModalState,
  (stepModalState) => {
    return stepModalState?.step;
  }
);
export const getCurrentAction = createSelector(
  getStepModalState,
  getFundingMethod,
  getRequestStatus,
  (stepModalState, fundingMethod, requestStates) => {
    const error = requestStates['updateMission'] === 'ERROR'
    if (error) {
      return 'MAINTENANCE_OPT_OUT_ERROR';
    }
    if (stepModalState.action === 'FUNDING') {
      return fundingMethod ? fundingMethod : 'DEFAULT';
    }
    return stepModalState?.action;
  }
);
